import React from 'react';
import { Button } from '@fluentui/react-components';
import { Edit20Regular } from '@fluentui/react-icons';
import { useBoolean } from '@fluentui/react-hooks';
import { useTranslation } from 'react-i18next';

// models
import { Person } from '../../../models/Entities/Person/Person';
import { PersonTypeEnum } from '../../../models/Entities/Person/PersonCommands';

// components
import PersonFormComponent from './Person.Form.Component';

// guía
import { GuideSectionTitle } from '../../../theme/components/GuideSectionTitle';

// functions
import * as CommonFunctions from '../Persons.Functions';

import './Person.Resume.Styles.css';

export interface IProps {
  Person: Person;
}

/**
 * Resumen de sólo lectura de la persona (guía): hechos en grilla de etiqueta arriba y valor
 * abajo, con "Editar datos personales" en el título. La edición va por el drawer de persona.
 */
export const PersonResumeComponent = (props: IProps) => {
  const [t] = useTranslation();
  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);

  const isJuridicalPerson = props.Person.personType === PersonTypeEnum.JuridicalPerson;

  const fact = (key: string, label: string, value: string | undefined | null, mono = false) => (
    <div className="prs-fact" key={key} data-testid={`prs-fact-${key}`}>
      <span className="prs-fact__label">{label}</span>
      <span className={`prs-fact__value${value ? '' : ' is-empty'}${mono ? ' prs-fact__value--mono' : ''}`}>{value || '—'}</span>
    </div>
  );

  return (
    <div className="prs" data-testid="person-resume">
      <GuideSectionTitle
        title={t('persons.resume.title', { defaultValue: 'Datos personales' })}
        description={t('persons.resume.description', { defaultValue: 'Cómo está registrada la persona. Los cambios se hacen desde Editar.' })}
        actions={
          <Button appearance="secondary" icon={<Edit20Regular />} onClick={openForm} data-testid="prs-edit">
            {t('persons.resume.edit', { defaultValue: 'Editar datos personales' })}
          </Button>
        }
      />

      <div className="prs-facts">
        {fact('personType', t('persons.form.personType', { defaultValue: 'Tipo de persona' }),
          isJuridicalPerson ? t('persons.personType.juridical', { defaultValue: 'Persona jurídica' }) : t('persons.personType.natural', { defaultValue: 'Persona física' }))}
        {fact('displayName', t('persons.resume.displayName', { defaultValue: 'Nombre' }), CommonFunctions.GetPersonDisplayName(props.Person))}
        {!isJuridicalPerson && fact('firstName', t('persons.form.firstName', { defaultValue: 'Nombre' }), props.Person.firstName)}
        {!isJuridicalPerson && fact('lastName', t('persons.form.lastName', { defaultValue: 'Apellido' }), props.Person.lastName)}
        {/* Fecha y lugar de nacimiento y nacionalidad no se muestran en el staff: son datos de paciente. */}
        {fact('activity', t('persons.form.activity', { defaultValue: 'Actividad' }), props.Person.activity)}
        {fact('personIdentifier', t('persons.resume.personIdentifier', { defaultValue: 'Identificador único' }), props.Person.personIdentifier, true)}
      </div>

      <PersonFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={dismissForm}
        personId={props.Person.id}
      />
    </div>
  );
};

export default PersonResumeComponent;
